import { Controller } from "@hotwired/stimulus"

export default class extends Controller<HTMLElement> {
  static targets = ["checkbox", "children", "count", "chevron", "totalCount", "clearButton"]

  declare readonly checkboxTargets: HTMLInputElement[]
  declare readonly totalCountTarget: HTMLElement
  declare readonly clearButtonTarget: HTMLElement

  connect() {
    this.checkboxTargets
      .filter((checkbox) => checkbox.checked)
      .sort((left, right) => this.nodeDepth(left) - this.nodeDepth(right))
      .forEach((checkbox) => this.setDescendants(checkbox, true))

    this.recalculate()
    this.expandSelectedPaths()
  }

  toggleNode(event: Event) {
    const checkbox = event.currentTarget as HTMLInputElement
    checkbox.indeterminate = false
    this.setDescendants(checkbox, checkbox.checked)
    this.recalculate()
  }

  toggleExpand(event: Event) {
    const node = (event.currentTarget as HTMLElement).closest<HTMLElement>("[data-area-tree-node]")
    if (!node) return

    const children = this.childrenContainer(node)
    if (!children) return

    const expanded = children.classList.toggle("hidden") === false
    this.setExpandedState(node, expanded)
  }

  clearAll(event: Event) {
    event.preventDefault()
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false
      checkbox.indeterminate = false
    })
    this.recalculate()
  }

  recalculate() {
    this.nodeElements
      .sort((left, right) => this.elementDepth(right) - this.elementDepth(left))
      .forEach((node) => this.recalculateNode(node))

    const total = this.checkboxTargets.filter((checkbox) => this.leafCheckbox(checkbox) && checkbox.checked).length
    this.totalCountTarget.textContent = `${total}件選択中`
    this.totalCountTarget.classList.toggle("hidden", total === 0)
    this.clearButtonTarget.classList.toggle("hidden", total === 0)
  }

  recalculateNode(node: HTMLElement) {
    const checkbox = this.nodeCheckbox(node)
    const leaves = this.descendantCheckboxes(node).filter((descendant) => this.leafCheckbox(descendant))
    if (!checkbox || leaves.length === 0) return

    const selectedCount = leaves.filter((leaf) => leaf.checked).length
    checkbox.checked = selectedCount === leaves.length
    checkbox.indeterminate = selectedCount > 0 && selectedCount < leaves.length

    const count = this.nodeCount(node)
    if (count) {
      count.textContent = `${selectedCount}件`
      count.classList.toggle("hidden", selectedCount === 0)
    }
  }

  setDescendants(checkbox: HTMLInputElement, checked: boolean) {
    const node = checkbox.closest<HTMLElement>("[data-area-tree-node]")
    if (!node) return

    this.descendantCheckboxes(node).forEach((descendant) => {
      descendant.checked = checked
      descendant.indeterminate = false
    })
  }

  expandSelectedPaths() {
    this.checkboxTargets
      .filter((checkbox) => this.leafCheckbox(checkbox) && checkbox.checked)
      .forEach((checkbox) => {
        let node = checkbox.closest<HTMLElement>("[data-area-tree-node]")
        while (node) {
          const parentNode = node.parentElement?.closest<HTMLElement>("[data-area-tree-node]") ?? null
          if (!parentNode) break

          this.expandNode(parentNode)
          node = parentNode
        }
      })
  }

  expandNode(node: HTMLElement) {
    const children = this.childrenContainer(node)
    if (!children) return

    children.classList.remove("hidden")
    this.setExpandedState(node, true)
  }

  setExpandedState(node: HTMLElement, expanded: boolean) {
    const chevron = this.nodeChevron(node)
    if (chevron) chevron.classList.toggle("rotate-180", expanded)

    const button = node.querySelector<HTMLElement>(":scope > [data-area-tree-row] button[aria-expanded]")
    if (button) button.setAttribute("aria-expanded", expanded.toString())
  }

  get nodeElements(): HTMLElement[] {
    return Array.from(this.element.querySelectorAll<HTMLElement>("[data-area-tree-node]"))
  }

  nodeDepth(checkbox: HTMLInputElement) {
    const node = checkbox.closest<HTMLElement>("[data-area-tree-node]")
    return node ? this.elementDepth(node) : 0
  }

  elementDepth(node: HTMLElement) {
    let depth = 0
    let parentNode = node.parentElement?.closest<HTMLElement>("[data-area-tree-node]") ?? null
    while (parentNode) {
      depth += 1
      parentNode = parentNode.parentElement?.closest<HTMLElement>("[data-area-tree-node]") ?? null
    }
    return depth
  }

  nodeCheckbox(node: HTMLElement) {
    return node.querySelector<HTMLInputElement>(":scope > [data-area-tree-row] [data-area-tree-target='checkbox']")
  }

  nodeCount(node: HTMLElement) {
    return node.querySelector<HTMLElement>(":scope > [data-area-tree-row] [data-area-tree-target='count']")
  }

  nodeChevron(node: HTMLElement) {
    return node.querySelector<HTMLElement>(":scope > [data-area-tree-row] [data-area-tree-target='chevron']")
  }

  childrenContainer(node: HTMLElement) {
    return node.querySelector<HTMLElement>(":scope > [data-area-tree-children]")
  }

  descendantCheckboxes(node: HTMLElement) {
    const children = this.childrenContainer(node)
    return children ? Array.from(children.querySelectorAll<HTMLInputElement>("[data-area-tree-target='checkbox']")) : []
  }

  leafCheckbox(checkbox: HTMLInputElement) {
    const node = checkbox.closest<HTMLElement>("[data-area-tree-node]")
    return node ? this.childrenContainer(node) === null : true
  }
}
